import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import RoleBasedNavigation from '../../components/navigation/RoleBasedNavigation';
import BreadcrumbTrail from '../../components/navigation/BreadcrumbTrail';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { getCategories, getAssets, updateAsset, bulkUpdateAssets, getAssetPublicUrl, deleteAssetWithStorage, addAsset } from '../../services/avatarAssetService';
import { previewZip, ingestZip, getIngestJobs, getIngestJobItems, exportFailedItemsCSV, validateAvatarBucket } from '../../services/avatarIngestService';
import { getRules, upsertRule, deleteRule } from '../../services/avatarRulesService';
import { getPresets, upsertPreset, deletePreset } from '../../services/avatarAssetService';
import { AVATAR_ASSET_BUCKET, AVATAR_ASSET_VERSION } from '../../config/storage';
import { supabase } from '../../lib/supabase';

const TABS = [
  { id: 'library', label: 'Asset Library', icon: 'Grid3X3' },
  { id: 'ingest', label: 'ZIP Ingest', icon: 'Upload' },
  { id: 'jobs', label: 'Ingest Jobs', icon: 'ClipboardList' },
  { id: 'rules', label: 'Rules Manager', icon: 'GitBranch' },
  { id: 'presets', label: 'Presets & Collections', icon: 'Sparkles' },
  { id: 'settings', label: 'Settings', icon: 'Settings' },
];

const STATUS_COLORS = {
  success: 'bg-green-500/20 text-green-400 border-green-500/30',
  failed: 'bg-red-500/20 text-red-400 border-red-500/30',
  skipped: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  ambiguous: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  pending: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  processing: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  completed: 'bg-green-500/20 text-green-400 border-green-500/30',
};

const CATEGORY_DEFAULT_LAYER_ORDER = {
  backgrounds: 1,
  bodies: 10,
  eyes: 40,
  eyebrows: 45,
  mouths: 50,
  hair_styles: 60,
  facial_hair: 65,
  clothing: 20,
  accessories: 90,
  hats: 97,
  fantasy: 95,
};

// ─── RARITY HELPERS ───────────────────────────────────────────────────────────

const RARITY_OPTIONS = ['common', 'rare', 'epic', 'legendary'];

const RARITY_CONFIG = {
  common:    { label: 'Common',    color: 'text-gray-400',   bg: 'bg-gray-500/15',   border: 'border-gray-500/30'   },
  rare:      { label: 'Rare',      color: 'text-blue-400',   bg: 'bg-blue-500/15',   border: 'border-blue-500/30'   },
  epic:      { label: 'Epic',      color: 'text-purple-400', bg: 'bg-purple-500/15', border: 'border-purple-500/30' },
  legendary: { label: 'Legendary', color: 'text-yellow-400', bg: 'bg-yellow-500/15', border: 'border-yellow-500/30' },
};

const RarityPill = ({ rarity }) => {
  const cfg = RARITY_CONFIG?.[rarity] || RARITY_CONFIG?.common;
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-semibold border ${cfg?.bg} ${cfg?.color} ${cfg?.border}`}>
      {rarity === 'legendary' && '✦ '}
      {cfg?.label}
    </span>
  );
};

const RaritySelector = ({ value, onChange }) => (
  <div>
    <label className="block text-sm text-muted-foreground mb-1">Rarity Tier</label>
    <select
      value={value || 'common'}
      onChange={e => onChange(e?.target?.value)}
      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
    >
      {RARITY_OPTIONS?.map(r => (
        <option key={r} value={r}>{r?.charAt(0)?.toUpperCase() + r?.slice(1)}</option>
      ))}
    </select>
    {value && <div className="mt-1.5"><RarityPill rarity={value} /></div>}
  </div>
);

// ─── UNLOCK REQUIREMENT FIELDS ────────────────────────────────────────────────

const UnlockRequirementFields = ({ value, onChange }) => {
  const unlockType = value?.unlockType || 'none';

  return (
    <div className="p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg space-y-3">
      <p className="text-xs font-medium text-amber-400 uppercase tracking-wide flex items-center gap-1.5">
        <Icon name="Lock" size={12} />
        Unlock Requirement
      </p>
      <div>
        <label className="block text-xs text-muted-foreground mb-1">Lock this asset until student achieves:</label>
        <select
          value={unlockType}
          onChange={e => onChange({ unlockType: e?.target?.value, unlockXp: '', unlockBadgeType: '', unlockBadgeName: '', unlockAchievementId: '' })}
          className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50">
          <option value="none">No lock — available to all students</option>
          <option value="xp">XP threshold</option>
          <option value="badge">Specific badge</option>
          <option value="achievement">Specific achievement</option>
        </select>
      </div>

      {unlockType === 'xp' && (
        <div>
          <label className="block text-xs text-muted-foreground mb-1">Minimum XP required <span className="text-red-400">*</span></label>
          <input
            type="number"
            min="1"
            value={value?.unlockXp || ''}
            onChange={e => onChange({ unlockXp: e?.target?.value })}
            placeholder="e.g. 250"
            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
          <p className="text-xs text-muted-foreground mt-1">Student must have at least this many XP points to use this asset.</p>
        </div>
      )}

      {unlockType === 'badge' && (
        <>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Badge type <span className="text-red-400">*</span></label>
            <input
              type="text"
              value={value?.unlockBadgeType || ''}
              onChange={e => onChange({ unlockBadgeType: e?.target?.value })}
              placeholder="e.g. xp, quiz, avatar, streak"
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Badge name <span className="text-red-400">*</span></label>
            <input
              type="text"
              value={value?.unlockBadgeName || ''}
              onChange={e => onChange({ unlockBadgeName: e?.target?.value })}
              placeholder="e.g. Rising Star"
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
          <p className="text-xs text-muted-foreground">Student must have earned a badge with this exact type and name.</p>
        </>
      )}

      {unlockType === 'achievement' && (
        <div>
          <label className="block text-xs text-muted-foreground mb-1">Achievement ID <span className="text-red-400">*</span></label>
          <input
            type="text"
            value={value?.unlockAchievementId || ''}
            onChange={e => onChange({ unlockAchievementId: e?.target?.value })}
            placeholder="UUID from achievements table"
            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
          <p className="text-xs text-muted-foreground mt-1">Student must have earned this achievement to use this asset.</p>
        </div>
      )}

      {unlockType !== 'none' && (
        <div className="flex items-start gap-2 p-2 bg-amber-500/10 border border-amber-500/20 rounded-lg">
          <Icon name="Info" size={12} className="text-amber-400 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-300">Locked assets appear in the student's avatar studio with a lock icon. Students can see them but cannot select them until the requirement is met.</p>
        </div>
      )}
    </div>
  );
};

// ─── ASSET LIBRARY TAB ────────────────────────────────────────────────────────

const AssetLibraryTab = ({ categories }) => {
  const [assets, setAssets] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [editingAsset, setEditingAsset] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editUploadFiles, setEditUploadFiles] = useState({}); // { main: File|null, hairBack: File|null, bodyHead: File|null }
  const [editUploading, setEditUploading] = useState(false);
  const [editUploadError, setEditUploadError] = useState(null);
  const [page, setPage] = useState(0);
  const [uploadingAssetId, setUploadingAssetId] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [cacheBustMap, setCacheBustMap] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null); // asset to delete
  const [deleting, setDeleting] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ categoryId: '', categorySlug: '', displayName: '', file: null, hairBackFile: null, bodyHeadFile: null, unlockType: 'none', unlockXp: '', unlockBadgeType: '', unlockBadgeName: '', unlockAchievementId: '', rarity: 'common' });
  const [addError, setAddError] = useState(null);
  const [adding, setAdding] = useState(false);
  // For per-asset hair_back upload
  const [hairBackUploadTarget, setHairBackUploadTarget] = useState(null);
  // For per-asset body_head upload
  const [bodyHeadUploadTarget, setBodyHeadUploadTarget] = useState(null);
  const fileInputRef = useRef(null);
  const addFileInputRef = useRef(null);
  const addHairBackFileInputRef = useRef(null);
  const addBodyHeadFileInputRef = useRef(null);
  const hairBackFileInputRef = useRef(null);
  const bodyHeadFileInputRef = useRef(null);
  const uploadTargetRef = useRef(null);
  const editMainFileInputRef = useRef(null);
  const editHairBackFileInputRef = useRef(null);
  const editBodyHeadFileInputRef = useRef(null);

  const fetchAssets = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getAssets({ categorySlug: selectedCategory || undefined, search: search || undefined, isArchived: false, page });
      setAssets(result?.assets);
      setTotal(result?.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, search, page]);

  useEffect(() => { fetchAssets(); }, [fetchAssets]);

  const handleBulkAction = async (action) => {
    if (selectedIds?.length === 0) return;
    try {
      const updates = {
        activate: { is_active: true },
        deactivate: { is_active: false },
        archive: { is_archived: true },
        show: { is_student_visible: true },
        hide: { is_student_visible: false },
      }?.[action];
      if (updates) {
        await bulkUpdateAssets(selectedIds, updates);
        setSelectedIds([]);
        fetchAssets();
      }
    } catch (err) { console.error(err); }
  };

  const handleEditSave = async () => {
    setEditUploading(true);
    setEditUploadError(null);
    try {
      const categorySlug = editingAsset?.avatar_asset_categories?.slug || '';
      const isHair = categorySlug === 'hair_styles';
      const isBody = categorySlug === 'bodies';

      // Upload main (hair_front / body_main / single) file if selected
      if (editUploadFiles?.main) {
        const file = editUploadFiles?.main;
        const storagePath = editingAsset?.storage_path;
        const { error: storageError } = await supabase?.storage
          ?.from('avatar-assets')
          ?.upload(storagePath, file, { contentType: file?.type || 'image/png', upsert: true });
        if (storageError) throw storageError;
        editForm.filename = file?.name;
        editForm.mime_type = file?.type || 'image/png';
      }

      // Upload hair_back if selected
      if (isHair && editUploadFiles?.hairBack) {
        const file = editUploadFiles?.hairBack;
        const ext = file?.name?.split('.')?.pop() || 'png';
        const backPath = editingAsset?.hair_back_storage_path || `v1/hair_styles/${editingAsset?.slug}_back.${ext}`;
        const { error: storageError } = await supabase?.storage
          ?.from('avatar-assets')
          ?.upload(backPath, file, { contentType: file?.type || 'image/png', upsert: true });
        if (storageError) throw storageError;
        editForm.hair_back_storage_path = backPath;
      }

      // Upload body_head if selected
      if (isBody && editUploadFiles?.bodyHead) {
        const file = editUploadFiles?.bodyHead;
        const ext = file?.name?.split('.')?.pop() || 'png';
        const headPath = editingAsset?.body_head_storage_path || `v1/bodies/${editingAsset?.slug}_head.${ext}`;
        const { error: storageError } = await supabase?.storage
          ?.from('avatar-assets')
          ?.upload(headPath, file, { contentType: file?.type || 'image/png', upsert: true });
        if (storageError) throw storageError;
        editForm.body_head_storage_path = headPath;
      }

      await updateAsset(editingAsset?.id, {
        ...editForm,
        unlock_requirement_type: editForm?.unlockType || 'none',
        unlock_xp_required: editForm?.unlockType === 'xp' ? parseInt(editForm?.unlockXp) || null : null,
        unlock_badge_type: editForm?.unlockType === 'badge' ? editForm?.unlockBadgeType?.trim() || null : null,
        unlock_badge_name: editForm?.unlockType === 'badge' ? editForm?.unlockBadgeName?.trim() || null : null,
        unlock_achievement_id: editForm?.unlockType === 'achievement' ? editForm?.unlockAchievementId || null : null,
        rarity: editForm?.rarity || 'common',
      });
      setCacheBustMap(prev => ({ ...prev, [editingAsset?.id]: Date.now() }));
      setEditingAsset(null);
      setEditUploadFiles({});
      fetchAssets();
    } catch (err) {
      console.error('Edit save failed:', err);
      setEditUploadError(err?.message || 'Save failed.');
    } finally {
      setEditUploading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev?.includes(id) ? prev?.filter(x => x !== id) : [...prev, id]);
  };

  // Upload hair_front (main storage_path) for any asset
  const handleUploadClick = (e, asset) => {
    e?.stopPropagation();
    setUploadError(null);
    uploadTargetRef.current = asset;
    fileInputRef.current.value = '';
    fileInputRef?.current?.click();
  };

  // Upload hair_back for a hair_styles asset
  const handleHairBackUploadClick = (e, asset) => {
    e?.stopPropagation();
    setUploadError(null);
    setHairBackUploadTarget(asset);
    hairBackFileInputRef.current.value = '';
    hairBackFileInputRef?.current?.click();
  };

  // Upload body_head for a bodies asset
  const handleBodyHeadUploadClick = (e, asset) => {
    e?.stopPropagation();
    setUploadError(null);
    setBodyHeadUploadTarget(asset);
    bodyHeadFileInputRef.current.value = '';
    bodyHeadFileInputRef?.current?.click();
  };

  const handleFileSelected = async (e) => {
    const file = e?.target?.files?.[0];
    const asset = uploadTargetRef?.current;
    if (!file || !asset) return;

    setUploadingAssetId(asset?.id);
    setUploadError(null);
    try {
      const storagePath = asset?.storage_path;
      const { error: storageError } = await supabase?.storage
        ?.from('avatar-assets')
        ?.upload(storagePath, file, { contentType: file?.type || 'image/png', upsert: true });
      if (storageError) throw storageError;

      await updateAsset(asset?.id, {
        filename: file?.name,
        mime_type: file?.type || 'image/png',
      });

      setCacheBustMap(prev => ({ ...prev, [asset?.id]: Date.now() }));
      fetchAssets();
    } catch (err) {
      console.error('Asset image upload failed:', err);
      setUploadError(`Upload failed for "${asset?.slug}": ${err?.message}`);
    } finally {
      setUploadingAssetId(null);
      uploadTargetRef.current = null;
    }
  };

  const handleHairBackFileSelected = async (e) => {
    const file = e?.target?.files?.[0];
    const asset = hairBackUploadTarget;
    if (!file || !asset) return;

    setUploadingAssetId(asset?.id);
    setUploadError(null);
    try {
      const ext = file?.name?.split('.')?.pop() || 'png';
      // Derive hair_back path from existing slug
      const backPath = asset?.hair_back_storage_path || `v1/hair_styles/${asset?.slug}_back.${ext}`;
      const { error: storageError } = await supabase?.storage
        ?.from('avatar-assets')
        ?.upload(backPath, file, { contentType: file?.type || 'image/png', upsert: true });
      if (storageError) throw storageError;

      await updateAsset(asset?.id, { hair_back_storage_path: backPath });
      setCacheBustMap(prev => ({ ...prev, [asset?.id]: Date.now() }));
      fetchAssets();
    } catch (err) {
      console.error('Hair back upload failed:', err);
      setUploadError(`Hair back upload failed for "${asset?.slug}": ${err?.message}`);
    } finally {
      setUploadingAssetId(null);
      setHairBackUploadTarget(null);
    }
  };

  const handleBodyHeadFileSelected = async (e) => {
    const file = e?.target?.files?.[0];
    const asset = bodyHeadUploadTarget;
    if (!file || !asset) return;

    setUploadingAssetId(asset?.id);
    setUploadError(null);
    try {
      const ext = file?.name?.split('.')?.pop() || 'png';
      const headPath = asset?.body_head_storage_path || `v1/bodies/${asset?.slug}_head.${ext}`;
      const { error: storageError } = await supabase?.storage
        ?.from('avatar-assets')
        ?.upload(headPath, file, { contentType: file?.type || 'image/png', upsert: true });
      if (storageError) throw storageError;

      await updateAsset(asset?.id, { body_head_storage_path: headPath });
      setCacheBustMap(prev => ({ ...prev, [asset?.id]: Date.now() }));
      fetchAssets();
    } catch (err) {
      console.error('Body head upload failed:', err);
      setUploadError(`Body head upload failed for "${asset?.slug}": ${err?.message}`);
    } finally {
      setUploadingAssetId(null);
      setBodyHeadUploadTarget(null);
    }
  };

  const handleDeleteClick = (e, asset) => {
    e?.stopPropagation();
    setDeleteConfirm(asset);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      // Also remove hair_back from storage if present
      if (deleteConfirm?.hair_back_storage_path) {
        await supabase?.storage?.from('avatar-assets')?.remove([deleteConfirm?.hair_back_storage_path]);
      }
      // Also remove body_head from storage if present
      if (deleteConfirm?.body_head_storage_path) {
        await supabase?.storage?.from('avatar-assets')?.remove([deleteConfirm?.body_head_storage_path]);
      }
      await deleteAssetWithStorage(deleteConfirm?.id, deleteConfirm?.storage_path);
      setDeleteConfirm(null);
      setSelectedIds(prev => prev?.filter(id => id !== deleteConfirm?.id));
      fetchAssets();
    } catch (err) {
      console.error('Delete failed:', err);
      setUploadError(`Delete failed: ${err?.message}`);
      setDeleteConfirm(null);
    } finally {
      setDeleting(false);
    }
  };

  const openAddModal = () => {
    const activeCat = selectedCategory
      ? categories?.find(c => c?.slug === selectedCategory)
      : categories?.[0];
    setAddForm({ categoryId: activeCat?.id || '', categorySlug: activeCat?.slug || '', displayName: '', file: null, hairBackFile: null, bodyHeadFile: null, unlockType: 'none', unlockXp: '', unlockBadgeType: '', unlockBadgeName: '', unlockAchievementId: '', rarity: 'common' });
    setAddError(null);
    setShowAddModal(true);
  };

  const handleAddCategoryChange = (e) => {
    const cat = categories?.find(c => c?.id === e?.target?.value);
    setAddForm(f => ({ ...f, categoryId: cat?.id || '', categorySlug: cat?.slug || '', hairBackFile: null, bodyHeadFile: null }));
  };

  const handleAddFileChange = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    setAddForm(f => ({
      ...f,
      file,
      displayName: f?.displayName || file?.name?.replace(/\.[^.]+$/, ''),
    }));
  };

  const handleAddHairBackFileChange = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    setAddForm(f => ({ ...f, hairBackFile: file }));
  };

  const handleAddBodyHeadFileChange = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    setAddForm(f => ({ ...f, bodyHeadFile: file }));
  };

  const handleAddSubmit = async () => {
    if (!addForm?.file) { setAddError('Please select a file.'); return; }
    if (!addForm?.categoryId) { setAddError('Please select a category.'); return; }
    if (addForm?.categorySlug === 'hair_styles' && !addForm?.hairBackFile) {
      setAddError('Hair assets require both a hair_front and a hair_back PNG file.'); return;
    }
    if (addForm?.categorySlug === 'bodies' && !addForm?.bodyHeadFile) {
      setAddError('Body assets require both a body_main and a body_head PNG file.'); return;
    }
    setAdding(true);
    setAddError(null);
    try {
      await addAsset({
        file: addForm?.file,
        hairBackFile: addForm?.hairBackFile || null,
        bodyHeadFile: addForm?.bodyHeadFile || null,
        categoryId: addForm?.categoryId,
        categorySlug: addForm?.categorySlug,
        displayName: addForm?.displayName?.trim() || undefined,
        unlockType: addForm?.unlockType || 'none',
        unlockXp: addForm?.unlockType === 'xp' ? parseInt(addForm?.unlockXp) || null : null,
        unlockBadgeType: addForm?.unlockType === 'badge' ? addForm?.unlockBadgeType?.trim() || null : null,
        unlockBadgeName: addForm?.unlockType === 'badge' ? addForm?.unlockBadgeName?.trim() || null : null,
        unlockAchievementId: addForm?.unlockType === 'achievement' ? addForm?.unlockAchievementId || null : null,
        rarity: addForm?.rarity || 'common',
      });
      setShowAddModal(false);
      fetchAssets();
    } catch (err) {
      console.error('Add asset failed:', err);
      setAddError(err?.message || 'Upload failed.');
    } finally {
      setAdding(false);
    }
  };

  const isHairCategory = (categorySlug) => categorySlug === 'hair_styles';
  const isBodyCategory = (categorySlug) => categorySlug === 'bodies';

  return (
    <div>
      {/* Hidden file inputs */}
      <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleFileSelected} />
      <input ref={hairBackFileInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleHairBackFileSelected} />
      <input ref={bodyHeadFileInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleBodyHeadFileSelected} />
      {/* Upload error banner */}
      {uploadError && (
        <div className="flex items-center gap-3 mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
          <Icon name="AlertCircle" size={16} className="text-red-400 shrink-0" />
          <span className="text-sm text-red-400 flex-1">{uploadError}</span>
          <button onClick={() => setUploadError(null)} className="text-red-400 hover:text-red-300">
            <Icon name="X" size={14} />
          </button>
        </div>
      )}
      {/* Top bar */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="flex-1 min-w-48">
          <input
            type="text"
            placeholder="Search by slug, name, tag..."
            value={search}
            onChange={e => { setSearch(e?.target?.value); setPage(0); }}
            className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </div>
        <Button variant="outline" size="sm" iconName="RefreshCw" onClick={fetchAssets}>Refresh</Button>
        <Button variant="default" size="sm" iconName="Plus" onClick={openAddModal}>Add Asset</Button>
      </div>
      {/* Category filter tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={() => { setSelectedCategory(''); setPage(0); }}
          className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${selectedCategory === '' ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border text-muted-foreground hover:text-foreground hover:border-primary/50'}`}
        >
          All
        </button>
        {categories?.map(c => (
          <button
            key={c?.id}
            onClick={() => { setSelectedCategory(c?.slug); setPage(0); }}
            className={`px-3 py-1.5 text-sm rounded-lg border transition-colors capitalize ${selectedCategory === c?.slug ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border text-muted-foreground hover:text-foreground hover:border-primary/50'}`}
          >
            {c?.label}
          </button>
        ))}
      </div>
      {/* Bulk Actions */}
      {selectedIds?.length > 0 && (
        <div className="flex items-center gap-3 mb-4 p-3 bg-primary/10 border border-primary/30 rounded-lg">
          <span className="text-sm text-primary font-medium">{selectedIds?.length} selected</span>
          <div className="flex gap-2 flex-wrap">
            {[
              { action: 'activate', label: 'Activate', icon: 'CheckCircle' },
              { action: 'deactivate', label: 'Deactivate', icon: 'XCircle' },
              { action: 'show', label: 'Show Students', icon: 'Eye' },
              { action: 'hide', label: 'Hide Students', icon: 'EyeOff' },
              { action: 'archive', label: 'Archive', icon: 'Archive' },
            ]?.map(({ action, label, icon }) => (
              <button key={action} onClick={() => handleBulkAction(action)}
                className="flex items-center gap-1 px-3 py-1 text-xs bg-card border border-border rounded-md hover:bg-muted transition-colors">
                <Icon name={icon} size={12} />
                {label}
              </button>
            ))}
            <button onClick={() => setSelectedIds([])}
              className="flex items-center gap-1 px-3 py-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
              Clear
            </button>
          </div>
        </div>
      )}
      {/* Asset Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : assets?.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <Icon name="ImageOff" size={48} className="mx-auto mb-3 opacity-30" />
          <p>No assets found. Upload a ZIP to get started.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {assets?.map(asset => {
            const isSelected = selectedIds?.includes(asset?.id);
            const isUploading = uploadingAssetId === asset?.id;
            const publicUrl = getAssetPublicUrl(asset?.storage_path);
            const cacheBust = asset?.updated_at ? new Date(asset.updated_at)?.getTime() : cacheBustMap?.[asset?.id];
            const thumbnailSrc = publicUrl ? (cacheBust ? `${publicUrl}?v=${cacheBust}` : publicUrl) : null;
            const isHair = asset?.avatar_asset_categories?.slug === 'hair_styles';
            const isBody = asset?.avatar_asset_categories?.slug === 'bodies';
            const hairBackUrl = isHair && asset?.hair_back_storage_path ? getAssetPublicUrl(asset?.hair_back_storage_path) : null;
            const hairBackThumb = hairBackUrl ? (cacheBust ? `${hairBackUrl}?v=${cacheBust}` : hairBackUrl) : null;
            const bodyHeadUrl = isBody && asset?.body_head_storage_path ? getAssetPublicUrl(asset?.body_head_storage_path) : null;
            const bodyHeadThumb = bodyHeadUrl ? (cacheBust ? `${bodyHeadUrl}?v=${cacheBust}` : bodyHeadUrl) : null;
            return (
              <div key={asset?.id}
                className={`relative group bg-card border rounded-xl overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-lg ${isSelected ? 'border-primary ring-2 ring-primary/30' : 'border-border'}`}
                onClick={() => toggleSelect(asset?.id)}>
                {/* Thumbnail — for hair, composite back+front */}
                <div className="aspect-square bg-muted/30 relative">
                  {isHair ? (
                    <>
                      {/* hair_back layer */}
                      {hairBackThumb && (
                        <img src={hairBackThumb} alt={`${asset?.slug} back`} className="absolute inset-0 w-full h-full object-contain p-2" style={{ zIndex: 1 }} onError={e => { e.currentTarget.style.display = 'none'; }} />
                      )}
                      {/* hair_front layer */}
                      {thumbnailSrc ? (
                        <img src={thumbnailSrc} alt={asset?.slug} className="absolute inset-0 w-full h-full object-contain p-2" style={{ zIndex: 2 }} />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center" style={{ zIndex: 2 }}>
                          <Icon name="Image" size={32} className="text-muted-foreground/30" />
                        </div>
                      )}
                      {/* Hair badge */}
                      <div className="absolute bottom-1 left-1 z-10">
                        <span className="px-1.5 py-0.5 text-xs bg-purple-500/80 text-white rounded leading-none">
                          {hairBackThumb ? '2-part' : 'front only'}
                        </span>
                      </div>
                    </>
                  ) : isBody ? (
                    <>
                      {/* body_main layer (behind) */}
                      {thumbnailSrc ? (
                        <img src={thumbnailSrc} alt={`${asset?.slug} main`} className="absolute inset-0 w-full h-full object-contain p-2" style={{ zIndex: 1 }} />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center" style={{ zIndex: 1 }}>
                          <Icon name="Image" size={32} className="text-muted-foreground/30" />
                        </div>
                      )}
                      {/* body_head layer (in front) */}
                      {bodyHeadThumb && (
                        <img src={bodyHeadThumb} alt={`${asset?.slug} head`} className="absolute inset-0 w-full h-full object-contain p-2" style={{ zIndex: 2 }} onError={e => { e.currentTarget.style.display = 'none'; }} />
                      )}
                      <div className="absolute bottom-1 left-1 z-10">
                        <span className="px-1.5 py-0.5 text-xs bg-blue-500/80 text-white rounded leading-none">
                          {bodyHeadThumb ? '2-part' : 'main only'}
                        </span>
                      </div>
                    </>
                  ) : thumbnailSrc ? (
                    <img src={thumbnailSrc} alt={asset?.slug} className="w-full h-full object-contain p-2" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Icon name="Image" size={32} className="text-muted-foreground/30" />
                    </div>
                  )}
                  {/* Upload overlay while uploading */}
                  {isUploading && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 backdrop-blur-sm rounded-t-xl z-20">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mb-1"></div>
                      <span className="text-xs text-white">Uploading…</span>
                    </div>
                  )}
                  {/* Status badges */}
                  <div className="absolute top-1 left-1 flex flex-col gap-1 z-10">
                    {!asset?.is_active && (
                      <span className="px-1.5 py-0.5 text-xs bg-red-500/80 text-white rounded">Off</span>
                    )}
                    {!asset?.is_student_visible && (
                      <span className="px-1.5 py-0.5 text-xs bg-orange-500/80 text-white rounded">Hidden</span>
                    )}
                    {asset?.is_archived && (
                      <span className="px-1.5 py-0.5 text-xs bg-gray-500/80 text-white rounded">Archived</span>
                    )}
                  </div>
                  {/* Action buttons (top-right) */}
                  <div className="absolute top-1 right-1 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                    {/* Edit metadata button */}
                    <button
                      onClick={e => { e?.stopPropagation(); setEditingAsset(asset); const categorySlug = asset?.avatar_asset_categories?.slug || ''; const defaultLayerOrder = CATEGORY_DEFAULT_LAYER_ORDER?.[categorySlug] ?? 50; setEditForm({ is_active: asset?.is_active, is_student_visible: asset?.is_student_visible, sort_order: asset?.sort_order, style_variant: asset?.style_variant || '', display_name: asset?.display_name || '', layer_order: asset?.layer_order != null ? asset?.layer_order : defaultLayerOrder, unlockType: asset?.unlock_requirement_type || 'none', unlockXp: asset?.unlock_xp_required || '', unlockBadgeType: asset?.unlock_badge_type || '', unlockBadgeName: asset?.unlock_badge_name || '', unlockAchievementId: asset?.unlock_achievement_id || '', rarity: asset?.rarity || 'common' }); setEditUploadFiles({}); setEditUploadError(null); }}
                      className="p-1 bg-card/90 rounded-md hover:bg-card transition-colors"
                      title="Edit metadata">
                      <Icon name="Pencil" size={12} />
                    </button>
                    {/* Upload hair_front (main image) */}
                    <button
                      onClick={e => handleUploadClick(e, asset)}
                      disabled={isUploading}
                      className="p-1 bg-card/90 rounded-md hover:bg-primary/80 hover:text-white transition-colors disabled:opacity-50"
                      title={isHair ? 'Upload hair_front PNG' : isBody ? 'Upload body_main PNG' : 'Upload new image'}>
                      <Icon name="ImagePlus" size={12} />
                    </button>
                    {/* Upload hair_back — only for hair_styles */}
                    {isHair && (
                      <button
                        onClick={e => handleHairBackUploadClick(e, asset)}
                        disabled={isUploading}
                        className="p-1 bg-card/90 rounded-md hover:bg-purple-500/80 hover:text-white transition-colors disabled:opacity-50"
                        title="Upload hair_back PNG">
                        <Icon name="Layers" size={12} />
                      </button>
                    )}
                    {/* Upload body_head — only for bodies */}
                    {isBody && (
                      <button
                        onClick={e => handleBodyHeadUploadClick(e, asset)}
                        disabled={isUploading}
                        className="p-1 bg-card/90 rounded-md hover:bg-blue-500/80 hover:text-white transition-colors disabled:opacity-50"
                        title="Upload body_head PNG">
                        <Icon name="Layers" size={12} />
                      </button>
                    )}
                    {/* Delete button */}
                    <button
                      onClick={e => handleDeleteClick(e, asset)}
                      className="p-1 bg-card/90 rounded-md hover:bg-red-500/80 hover:text-white transition-colors"
                      title="Delete asset">
                      <Icon name="Trash2" size={12} />
                    </button>
                  </div>
                </div>
                <div className="p-2">
                  <p className="text-xs font-medium text-foreground truncate">{asset?.display_name || asset?.slug}</p>
                  <p className="text-xs text-muted-foreground truncate">{asset?.avatar_asset_categories?.label || asset?.category_id}</p>
                  <div className="mt-1">
                    <RarityPill rarity={asset?.rarity || 'common'} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {/* Pagination */}
      <div className="flex items-center justify-between mt-6">
        <p className="text-sm text-muted-foreground">Showing {assets?.length} of {total} assets</p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>Previous</Button>
          <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={(page + 1) * 50 >= total}>Next</Button>
        </div>
      </div>
      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => !deleting && setDeleteConfirm(null)}>
          <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-sm mx-4 shadow-2xl" onClick={e => e?.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-500/10 rounded-lg">
                <Icon name="Trash2" size={20} className="text-red-400" />
              </div>
              <h3 className="text-lg font-bold text-foreground">Delete Asset</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-2">Are you sure you want to delete this asset? This will remove it from both the database and storage.</p>
            {deleteConfirm?.hair_back_storage_path && (
              <p className="text-xs text-purple-400 mb-2">⚠ This hair asset has a hair_back piece — both files will be deleted.</p>
            )}
            {deleteConfirm?.body_head_storage_path && (
              <p className="text-xs text-blue-400 mb-2">⚠ This body asset has a body_head piece — both files will be deleted.</p>
            )}
            <p className="text-xs font-mono text-foreground bg-muted/30 px-3 py-2 rounded-lg mb-6 truncate">{deleteConfirm?.slug}</p>
            <div className="flex gap-3">
              <button
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50">
                {deleting ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> : <Icon name="Trash2" size={14} />}
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-card border border-border text-foreground text-sm font-medium rounded-lg hover:bg-muted transition-colors disabled:opacity-50">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Add Asset Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => !adding && setShowAddModal(false)}>
          <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e?.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-foreground">Add Asset</h3>
              <button onClick={() => setShowAddModal(false)} disabled={adding} className="text-muted-foreground hover:text-foreground">
                <Icon name="X" size={20} />
              </button>
            </div>

            {addError && (
              <div className="flex items-center gap-2 mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <Icon name="AlertCircle" size={14} className="text-red-400 shrink-0" />
                <span className="text-sm text-red-400">{addError}</span>
              </div>
            )}

            <div className="space-y-4">
              {/* Category dropdown */}
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Category <span className="text-red-400">*</span></label>
                <select
                  value={addForm?.categoryId}
                  onChange={handleAddCategoryChange}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50">
                  <option value="">— Select category —</option>
                  {categories?.map(c => (
                    <option key={c?.id} value={c?.id}>{c?.label}</option>
                  ))}
                </select>
              </div>

              {/* Hair dual-part notice */}
              {isHairCategory(addForm?.categorySlug) && (
                <div className="flex items-start gap-2 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                  <Icon name="Layers" size={14} className="text-purple-400 shrink-0 mt-0.5" />
                  <p className="text-xs text-purple-300">
                    Hair assets require <strong>two PNG files</strong>: a <strong>hair_front</strong> (rendered above accessories) and a <strong>hair_back</strong> (rendered behind the body). Both are shown together as one asset.
                  </p>
                </div>
              )}

              {/* Body dual-part notice */}
              {isBodyCategory(addForm?.categorySlug) && (
                <div className="flex items-start gap-2 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <Icon name="Layers" size={14} className="text-blue-400 shrink-0 mt-0.5" />
                  <p className="text-xs text-blue-300">
                    Body assets require <strong>two PNG files</strong>: a <strong>body_main</strong> (rendered above hair_back, below clothing) and a <strong>body_head</strong> (rendered just behind the eyes). Both are shown together as one asset.
                  </p>
                </div>
              )}

              {/* Main file picker */}
              <div>
                <label className="block text-sm text-muted-foreground mb-1">
                  {isHairCategory(addForm?.categorySlug) ? 'hair_front PNG' : isBodyCategory(addForm?.categorySlug) ? 'body_main PNG' : 'Image File'} <span className="text-red-400">*</span>
                </label>
                <div
                  onClick={() => addFileInputRef?.current?.click()}
                  className="flex items-center gap-3 px-4 py-3 bg-background border border-dashed border-border rounded-lg cursor-pointer hover:border-primary/60 transition-colors">
                  <Icon name="Upload" size={16} className="text-muted-foreground shrink-0" />
                  <span className="text-sm text-muted-foreground truncate">
                    {addForm?.file ? addForm?.file?.name : isHairCategory(addForm?.categorySlug) ? 'Click to select hair_front PNG' : isBodyCategory(addForm?.categorySlug) ? 'Click to select body_main PNG' : 'Click to select PNG, JPEG, or WebP'}
                  </span>
                </div>
                <input
                  ref={addFileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={handleAddFileChange}
                />
              </div>

              {/* hair_back file picker — only for hair_styles */}
              {isHairCategory(addForm?.categorySlug) && (
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">
                    hair_back PNG <span className="text-red-400">*</span>
                  </label>
                  <div
                    onClick={() => addHairBackFileInputRef?.current?.click()}
                    className="flex items-center gap-3 px-4 py-3 bg-background border border-dashed border-purple-500/40 rounded-lg cursor-pointer hover:border-purple-500/70 transition-colors">
                    <Icon name="Layers" size={16} className="text-purple-400 shrink-0" />
                    <span className="text-sm text-muted-foreground truncate">
                      {addForm?.hairBackFile ? addForm?.hairBackFile?.name : 'Click to select hair_back PNG'}
                    </span>
                  </div>
                  <input
                    ref={addHairBackFileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={handleAddHairBackFileChange}
                  />
                </div>
              )}

              {/* body_head file picker — only for bodies */}
              {isBodyCategory(addForm?.categorySlug) && (
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">
                    body_head PNG <span className="text-red-400">*</span>
                  </label>
                  <div
                    onClick={() => addBodyHeadFileInputRef?.current?.click()}
                    className="flex items-center gap-3 px-4 py-3 bg-background border border-dashed border-blue-500/40 rounded-lg cursor-pointer hover:border-blue-500/70 transition-colors">
                    <Icon name="Layers" size={16} className="text-blue-400 shrink-0" />
                    <span className="text-sm text-muted-foreground truncate">
                      {addForm?.bodyHeadFile ? addForm?.bodyHeadFile?.name : 'Click to select body_head PNG'}
                    </span>
                  </div>
                  <input
                    ref={addBodyHeadFileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={handleAddBodyHeadFileChange}
                  />
                </div>
              )}

              {/* Display name */}
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Display Name <span className="text-xs text-muted-foreground">(optional — used as slug base)</span></label>
                <input
                  type="text"
                  value={addForm?.displayName}
                  onChange={e => setAddForm(f => ({ ...f, displayName: e?.target?.value }))}
                  placeholder="e.g. forest_meadow"
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>

              {/* ── Unlock Requirement ── */}
              <UnlockRequirementFields
                value={addForm}
                onChange={patch => setAddForm(f => ({ ...f, ...patch }))}
              />

              {/* ── Rarity Tier ── */}
              <RaritySelector
                value={addForm?.rarity}
                onChange={val => setAddForm(f => ({ ...f, rarity: val }))}
              />
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleAddSubmit}
                disabled={adding || !addForm?.file || !addForm?.categoryId || (isHairCategory(addForm?.categorySlug) && !addForm?.hairBackFile) || (isBodyCategory(addForm?.categorySlug) && !addForm?.bodyHeadFile)}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium rounded-lg transition-colors disabled:opacity-50">
                {adding ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> : <Icon name="Upload" size={14} />}
                {adding ? 'Uploading…' : 'Upload Asset'}
              </button>
              <button
                onClick={() => setShowAddModal(false)}
                disabled={adding}
                className="flex-1 px-4 py-2 bg-card border border-border text-foreground text-sm font-medium rounded-lg hover:bg-muted transition-colors disabled:opacity-50">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Edit Drawer */}
      {editingAsset && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => !editUploading && setEditingAsset(null)}>
          <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e?.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-foreground">Edit Asset</h3>
              <button onClick={() => !editUploading && setEditingAsset(null)} className="text-muted-foreground hover:text-foreground">
                <Icon name="X" size={20} />
              </button>
            </div>
            <p className="text-sm text-muted-foreground font-mono mb-4 truncate">{editingAsset?.slug}</p>

            {editUploadError && (
              <div className="flex items-center gap-2 mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <Icon name="AlertCircle" size={14} className="text-red-400 shrink-0" />
                <span className="text-sm text-red-400">{editUploadError}</span>
              </div>
            )}

            {/* Hidden file inputs for edit modal */}
            <input ref={editMainFileInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
              onChange={e => { const f = e?.target?.files?.[0]; if (f) setEditUploadFiles(prev => ({ ...prev, main: f })); }} />
            <input ref={editHairBackFileInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
              onChange={e => { const f = e?.target?.files?.[0]; if (f) setEditUploadFiles(prev => ({ ...prev, hairBack: f })); }} />
            <input ref={editBodyHeadFileInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
              onChange={e => { const f = e?.target?.files?.[0]; if (f) setEditUploadFiles(prev => ({ ...prev, bodyHead: f })); }} />

            <div className="space-y-4">
              {/* PNG Upload Section */}
              <div className="p-3 bg-muted/20 border border-border rounded-lg space-y-3">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                  <Icon name="ImagePlus" size={12} />
                  Replace Image Files
                </p>

                {/* Main file (hair_front / body_main / single) */}
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">
                    {editingAsset?.avatar_asset_categories?.slug === 'hair_styles' ?'hair_front PNG'
                      : editingAsset?.avatar_asset_categories?.slug === 'bodies' ?'body_main PNG' :'Image PNG'}
                    {editingAsset?.storage_path && (
                      <span className="ml-1 text-green-400">✓ exists</span>
                    )}
                  </label>
                  <div
                    onClick={() => editMainFileInputRef?.current?.click()}
                    className="flex items-center gap-2 px-3 py-2 bg-background border border-dashed border-border rounded-lg cursor-pointer hover:border-primary/60 transition-colors">
                    <Icon name="Upload" size={14} className="text-muted-foreground shrink-0" />
                    <span className="text-xs text-muted-foreground truncate">
                      {editUploadFiles?.main ? editUploadFiles?.main?.name : 'Click to select replacement PNG'}
                    </span>
                    {editUploadFiles?.main && (
                      <button
                        onClick={e => { e?.stopPropagation(); setEditUploadFiles(prev => ({ ...prev, main: null })); }}
                        className="ml-auto text-muted-foreground hover:text-red-400 shrink-0">
                        <Icon name="X" size={12} />
                      </button>
                    )}
                  </div>
                </div>

                {/* hair_back file picker — only for hair_styles */}
                {editingAsset?.avatar_asset_categories?.slug === 'hair_styles' && (
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">
                      hair_back PNG
                      {editingAsset?.hair_back_storage_path
                        ? <span className="ml-1 text-green-400">✓ exists</span>
                        : <span className="ml-1 text-orange-400">⚠ missing</span>}
                    </label>
                    <div
                      onClick={() => editHairBackFileInputRef?.current?.click()}
                      className="flex items-center gap-2 px-3 py-2 bg-background border border-dashed border-purple-500/40 rounded-lg cursor-pointer hover:border-purple-500/70 transition-colors">
                      <Icon name="Layers" size={14} className="text-purple-400 shrink-0" />
                      <span className="text-xs text-muted-foreground truncate">
                        {editUploadFiles?.hairBack ? editUploadFiles?.hairBack?.name : 'Click to select hair_back PNG'}
                      </span>
                      {editUploadFiles?.hairBack && (
                        <button
                          onClick={e => { e?.stopPropagation(); setEditUploadFiles(prev => ({ ...prev, hairBack: null })); }}
                          className="ml-auto text-muted-foreground hover:text-red-400 shrink-0">
                          <Icon name="X" size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* body_head file picker — only for bodies */}
                {editingAsset?.avatar_asset_categories?.slug === 'bodies' && (
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">
                      body_head PNG
                      {editingAsset?.body_head_storage_path
                        ? <span className="ml-1 text-green-400">✓ exists</span>
                        : <span className="ml-1 text-orange-400">⚠ missing</span>}
                    </label>
                    <div
                      onClick={() => editBodyHeadFileInputRef?.current?.click()}
                      className="flex items-center gap-2 px-3 py-2 bg-background border border-dashed border-blue-500/40 rounded-lg cursor-pointer hover:border-blue-500/70 transition-colors">
                      <Icon name="Layers" size={14} className="text-blue-400 shrink-0" />
                      <span className="text-xs text-muted-foreground truncate">
                        {editUploadFiles?.bodyHead ? editUploadFiles?.bodyHead?.name : 'Click to select body_head PNG'}
                      </span>
                      {editUploadFiles?.bodyHead && (
                        <button
                          onClick={e => { e?.stopPropagation(); setEditUploadFiles(prev => ({ ...prev, bodyHead: null })); }}
                          className="ml-auto text-muted-foreground hover:text-red-400 shrink-0">
                          <Icon name="X" size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm text-muted-foreground mb-1">Display Name</label>
                <input type="text" value={editForm?.display_name} onChange={e => setEditForm(f => ({ ...f, display_name: e?.target?.value }))}
                  placeholder="e.g. Forest Meadow Background"
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50" />
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={editForm?.is_active} onChange={e => setEditForm(f => ({ ...f, is_active: e?.target?.checked }))} className="w-4 h-4 rounded" />
                <span className="text-sm text-foreground">Active</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={editForm?.is_student_visible} onChange={e => setEditForm(f => ({ ...f, is_student_visible: e?.target?.checked }))} className="w-4 h-4 rounded" />
                <span className="text-sm text-foreground">Student Visible</span>
              </label>
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Style Variant</label>
                <input type="text" value={editForm?.style_variant} onChange={e => setEditForm(f => ({ ...f, style_variant: e?.target?.value }))}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm" />
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Sort Order</label>
                <input type="number" value={editForm?.sort_order} onChange={e => setEditForm(f => ({ ...f, sort_order: parseInt(e?.target?.value) || 0 }))}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm" />
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Layer Order <span className="text-xs text-muted-foreground">(controls Z-depth in avatar preview)</span></label>
                <input type="number" value={editForm?.layer_order} onChange={e => setEditForm(f => ({ ...f, layer_order: parseInt(e?.target?.value) || 0 }))}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm" />
              </div>

              {/* ── Unlock Requirement ── */}
              <UnlockRequirementFields
                value={editForm}
                onChange={patch => setEditForm(f => ({ ...f, ...patch }))}
              />
            </div>
            <div className="flex gap-3 mt-6">
              <Button variant="default" fullWidth onClick={handleEditSave} disabled={editUploading}>
                {editUploading ? (
                  <span className="flex items-center gap-2 justify-center">
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Saving…
                  </span>
                ) : 'Save Changes'}
              </Button>
              <Button variant="outline" fullWidth onClick={() => !editUploading && setEditingAsset(null)} disabled={editUploading}>Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── ZIP INGEST TAB ───────────────────────────────────────────────────────────

// Parser status badge helper
const ParserStatusBadge = ({ status }) => {
  let config = {
    valid: { color: 'bg-green-500/20 text-green-400 border-green-500/30', label: 'Valid' },
    warning: { color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', label: 'Warning' },
    review_required: { color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', label: 'Review' },
    invalid: { color: 'bg-red-500/20 text-red-400 border-red-500/30', label: 'Invalid' },
  };
  const c = config?.[status] || config?.invalid;
  return (<span className={`px-2 py-1 text-xs rounded-full border font-medium ${c?.color}`}>{c?.label}</span>);
};

// Inline metadata editor for review_required / warning files
const MetadataEditor = ({ preview, onSave, onClose }) => {
  const [form, setForm] = useState({
    category: preview?.category || '',
    style_variant: preview?.style_variant || '',
    color_family: preview?.color_family || '',
    subcategory: preview?.subcategory || '',
    variant: preview?.variant || '',
    gender_affinity: preview?.gender_affinity || '',
    body_type_affinity: preview?.body_type_affinity || '',
  });

  const CATEGORIES = [
    'bodies', 'eyes', 'eyebrows', 'mouths',
    'hair_styles', 'facial_hair', 'clothing', 'accessories', 'fantasy',
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl" onClick={e => e?.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-foreground">Edit Metadata</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><Icon name="X" size={18} /></button>
        </div>
        <p className="text-xs text-muted-foreground font-mono mb-4 truncate">{preview?.relativePath}</p>

        {/* Warnings */}
        {preview?.parser_warnings?.length > 0 && (
          <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
            {preview?.parser_warnings?.map((w, i) => (
              <p key={i} className="text-xs text-amber-400">{w}</p>
            ))}
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="block text-sm text-muted-foreground mb-1">Category *</label>
            <select value={form?.category} onChange={e => setForm(f => ({ ...f, category: e?.target?.value }))}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm">
              <option value="">— Select —</option>
              {CATEGORIES?.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Style Variant</label>
              <input type="text" value={form?.style_variant} onChange={e => setForm(f => ({ ...f, style_variant: e?.target?.value }))}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm" />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Color Family</label>
              <input type="text" value={form?.color_family} onChange={e => setForm(f => ({ ...f, color_family: e?.target?.value }))}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm" />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Subcategory</label>
              <input type="text" value={form?.subcategory} onChange={e => setForm(f => ({ ...f, subcategory: e?.target?.value }))}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm" />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Variant</label>
              <input type="text" value={form?.variant} onChange={e => setForm(f => ({ ...f, variant: e?.target?.value }))}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm" />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Gender Affinity</label>
              <select value={form?.gender_affinity} onChange={e => setForm(f => ({ ...f, gender_affinity: e?.target?.value }))}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm">
                <option value="">None</option>
                <option value="male">male</option>
                <option value="female">female</option>
                <option value="neutral">neutral</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Body Type</label>
              <select value={form?.body_type_affinity} onChange={e => setForm(f => ({ ...f, body_type_affinity: e?.target?.value }))}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm">
                <option value="">None</option>
                <option value="male">male</option>
                <option value="female">female</option>
                <option value="neutral">neutral</option>
              </select>
            </div>
          </div>
        </div>
        <div className="flex gap-3 mt-5">
          <Button variant="default" fullWidth onClick={() => onSave(form)}>Apply Corrections</Button>
          <Button variant="outline" fullWidth onClick={onClose}>Cancel</Button>
        </div>
      </div>
    </div>
  );
};

const ZipIngestTab = ({ userId }) => {
  const [dragOver, setDragOver] = useState(false);
  const [zipFile, setZipFile] = useState(null);
  const [previews, setPreviews] = useState([]);
  const [previewing, setPreviewing] = useState(false);
  const [ingesting, setIngesting] = useState(false);
  const [ingestProgress, setIngestProgress] = useState(null);
  const [ingestResult, setIngestResult] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [skippedPaths, setSkippedPaths] = useState(new Set());
  const [editingPreview, setEditingPreview] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const fileInputRef = useRef();

  const handleFile = async (file) => {
    if (!file?.name?.endsWith('.zip')) return;
    setZipFile(file);
    setIngestResult(null);
    setOverrides({});
    setSkippedPaths(new Set());
    setPreviewing(true);
    try {
      const result = await previewZip(file);
      setPreviews(result);
    } catch (err) {
      console.error(err);
    } finally {
      setPreviewing(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setDragOver(false);
    const file = e?.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  };

  const handleIngest = async () => {
    if (!zipFile) return;
    setIngesting(true);
    setIngestProgress({ current: 0, total: 0, filename: '' });
    try {
      const result = await ingestZip(
        zipFile,
        userId,
        (prog) => setIngestProgress(prog),
        overrides,
        skippedPaths
      );
      setIngestResult(result);
    } catch (err) {
      console.error(err);
    } finally {
      setIngesting(false);
    }
  };

  const toggleSkip = (relativePath) => {
    setSkippedPaths(prev => {
      const next = new Set(prev);
      if (next?.has(relativePath)) next?.delete(relativePath);
      else next?.add(relativePath);
      return next;
    });
  };

  const handleSaveOverride = (relativePath, form) => {
    setOverrides(prev => ({ ...prev, [relativePath]: form }));
    // Update preview display
    setPreviews(prev => prev?.map(p =>
      p?.relativePath === relativePath
        ? { ...p, ...form, parser_status: 'valid', parser_warnings: [...(p?.parser_warnings || []), 'Manually corrected.'] }
        : p
    ));
    setEditingPreview(null);
  };

  // Filtered previews
  const filteredPreviews = previews?.filter(p => {
    if (filterStatus === 'all') return true;
    if (filterStatus === 'valid') return p?.parser_status === 'valid';
    if (filterStatus === 'warning') return p?.parser_status === 'warning';
    if (filterStatus === 'review') return p?.parser_status === 'review_required';
    if (filterStatus === 'invalid') return p?.parser_status === 'invalid';
    return true;
  });

  // Summary counts
  const counts = {
    total: previews?.length,
    valid: previews?.filter(p => p?.parser_status === 'valid')?.length,
    warning: previews?.filter(p => p?.parser_status === 'warning')?.length,
    review: previews?.filter(p => p?.parser_status === 'review_required')?.length,
    invalid: previews?.filter(p => p?.parser_status === 'invalid')?.length,
  };

  const ingestableCount = previews?.filter(p =>
    !skippedPaths?.has(p?.relativePath) &&
    p?.parser_status !== 'invalid' &&
    (p?.parser_status !== 'review_required' || overrides?.[p?.relativePath])
  )?.length;

  const reviewItems = previews?.filter(p => p?.parser_status === 'review_required');

  return (
    <div className="max-w-5xl">
      {/* Drop Zone */}
      <div
        onDragOver={e => { e?.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef?.current?.click()}
        className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all duration-200 ${dragOver ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50 hover:bg-muted/30'}`}>
        <input ref={fileInputRef} type="file" accept=".zip" className="hidden" onChange={e => handleFile(e?.target?.files?.[0])} />
        <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Icon name="Upload" size={32} className="text-primary" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">Drop your ZIP file here</h3>
        <p className="text-muted-foreground text-sm">or click to browse — supports .zip files containing JPG/PNG/WebP assets</p>
        {zipFile && <p className="mt-3 text-sm text-primary font-medium">📦 {zipFile?.name}</p>}
      </div>
      {/* Preview Loading */}
      {previewing && (
        <div className="mt-6 flex items-center gap-3 text-muted-foreground">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
          <span>Analyzing ZIP contents with parser v1.0.0…</span>
        </div>
      )}
      {/* Preview Results */}
      {previews?.length > 0 && !previewing && (
        <div className="mt-6 space-y-5">

          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {[
              { label: 'Total', value: counts?.total, color: 'text-foreground', filter: 'all' },
              { label: 'Parsed OK', value: counts?.valid, color: 'text-green-400', filter: 'valid' },
              { label: 'Warnings', value: counts?.warning, color: 'text-amber-400', filter: 'warning' },
              { label: 'Needs Review', value: counts?.review, color: 'text-orange-400', filter: 'review' },
              { label: 'Invalid', value: counts?.invalid, color: 'text-red-400', filter: 'invalid' },
            ]?.map(({ label, value, color, filter }) => (
              <button key={label}
                onClick={() => setFilterStatus(filterStatus === filter ? 'all' : filter)}
                className={`bg-card border rounded-xl p-3 text-center transition-all hover:shadow-md ${filterStatus === filter ? 'border-primary ring-1 ring-primary/30' : 'border-border'}`}>
                <p className={`text-2xl font-bold ${color}`}>{value}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
              </button>
            ))}
          </div>

          {/* Ambiguous / Review Required Section */}
          {reviewItems?.length > 0 && (
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4">
              <h4 className="font-semibold text-orange-400 mb-3 flex items-center gap-2">
                <Icon name="AlertTriangle" size={16} />
                {reviewItems?.length} File{reviewItems?.length !== 1 ? 's' : ''} Need Review
              </h4>
              <div className="space-y-2">
                {reviewItems?.map((p, i) => (
                  <div key={i} className={`flex items-start gap-3 p-3 rounded-lg ${overrides?.[p?.relativePath] ? 'bg-green-500/10 border border-green-500/20' : 'bg-card/60'}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-mono text-foreground truncate">{p?.relativePath}</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {p?.parser_warnings?.map((w, wi) => (
                          <span key={wi} className="text-xs text-orange-300/80">{w}</span>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {overrides?.[p?.relativePath] && (
                        <span className="text-xs text-green-400 flex items-center gap-1">
                          <Icon name="CheckCircle" size={12} /> Corrected
                        </span>
                      )}
                      <button
                        onClick={() => setEditingPreview(p)}
                        className="flex items-center gap-1 px-2 py-1 text-xs bg-orange-500/20 text-orange-400 border border-orange-500/30 rounded-md hover:bg-orange-500/30 transition-colors">
                        <Icon name="Pencil" size={11} />
                        Edit
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* File Preview Table */}
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-border/50">
              <h4 className="font-semibold text-foreground">
                File Preview
                {filterStatus !== 'all' && <span className="ml-2 text-xs text-muted-foreground">(filtered: {filterStatus})</span>}
              </h4>
              <span className="text-xs text-muted-foreground">{filteredPreviews?.length} of {previews?.length} files</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/50 bg-muted/20">
                    <th className="text-left px-4 py-2 text-xs text-muted-foreground font-medium w-8">
                      <Icon name="Check" size={12} />
                    </th>
                    <th className="text-left px-4 py-2 text-xs text-muted-foreground font-medium">Filename</th>
                    <th className="text-left px-4 py-2 text-xs text-muted-foreground font-medium">Category</th>
                    <th className="text-left px-4 py-2 text-xs text-muted-foreground font-medium">Display Label</th>
                    <th className="text-left px-4 py-2 text-xs text-muted-foreground font-medium">Key Metadata</th>
                    <th className="text-left px-4 py-2 text-xs text-muted-foreground font-medium">Status</th>
                    <th className="text-left px-4 py-2 text-xs text-muted-foreground font-medium w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPreviews?.map((p, i) => {
                    const isSkipped = skippedPaths?.has(p?.relativePath);
                    const hasOverride = !!overrides?.[p?.relativePath];
                    const needsHighlight = p?.parser_status === 'warning' || p?.parser_status === 'review_required';
                    return (
                      <tr key={i}
                        className={`border-b border-border/30 last:border-0 transition-colors ${isSkipped ? 'opacity-40' : ''} ${needsHighlight && !isSkipped ? 'bg-amber-500/5' : 'hover:bg-muted/10'}`}>
                        {/* Skip checkbox */}
                        <td className="px-4 py-2.5">
                          <input
                            type="checkbox"
                            checked={!isSkipped}
                            onChange={() => toggleSkip(p?.relativePath)}
                            className="w-4 h-4 rounded accent-primary cursor-pointer"
                            title={isSkipped ? 'Include this file' : 'Skip this file'}
                          />
                        </td>
                        {/* Filename */}
                        <td className="px-4 py-2.5 max-w-[200px]">
                          <p className="font-mono text-xs text-foreground truncate" title={p?.relativePath}>{p?.filename}</p>
                          {p?.relativePath !== p?.filename && (
                            <p className="text-xs text-muted-foreground truncate">{p?.relativePath?.split('/')?.slice(0, -1)?.join('/')}</p>
                          )}
                        </td>
                        {/* Category */}
                        <td className="px-4 py-2.5">
                          <span className="text-xs text-foreground font-medium">{p?.category || <span className="text-muted-foreground italic">unknown</span>}</span>
                        </td>
                        {/* Display Label */}
                        <td className="px-4 py-2.5 max-w-[160px]">
                          <span className="text-xs text-foreground truncate block">{p?.display_label || '—'}</span>
                        </td>
                        {/* Key Metadata */}
                        <td className="px-4 py-2.5">
                          <div className="flex flex-wrap gap-1">
                            {[
                              p?.style_variant && `style: ${p?.style_variant}`,
                              p?.color_family && `color: ${p?.color_family}`,
                              p?.eye_color && `eye: ${p?.eye_color}`,
                              p?.hair_color && `hair: ${p?.hair_color}`,
                              p?.gender_affinity && `gender: ${p?.gender_affinity}`,
                              p?.body_type_affinity && `body: ${p?.body_type_affinity}`,
                              p?.special_type && `type: ${p?.special_type}`,
                              p?.subcategory && `sub: ${p?.subcategory}`,
                              p?.variant && `var: ${p?.variant}`,
                            ]?.filter(Boolean)?.slice(0, 3)?.map((tag, ti) => (
                              <span key={ti} className="px-1.5 py-0.5 text-xs bg-muted/50 text-muted-foreground rounded">{tag}</span>
                            ))}
                          </div>
                        </td>
                        {/* Status */}
                        <td className="px-4 py-2.5">
                          {hasOverride
                            ? <span className="px-2 py-0.5 text-xs rounded-full border bg-green-500/20 text-green-400 border-green-500/30 font-medium">Corrected</span>
                            : <ParserStatusBadge status={p?.parser_status} />
                          }
                        </td>
                        {/* Edit button */}
                        <td className="px-4 py-2.5">
                          {(p?.parser_status === 'review_required' || p?.parser_status === 'warning') && (
                            <button
                              onClick={() => setEditingPreview(p)}
                              className="p-1.5 hover:bg-muted rounded-md transition-colors text-muted-foreground hover:text-foreground"
                              title="Edit metadata">
                              <Icon name="Pencil" size={13} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Ingest Controls */}
          {!ingestResult && (
            <div className="flex items-center gap-3 flex-wrap">
              <Button
                variant="default"
                iconName="Zap"
                onClick={handleIngest}
                disabled={ingesting || ingestableCount === 0}>
                {ingesting ? 'Ingesting…' : `Ingest ${ingestableCount} File${ingestableCount !== 1 ? 's' : ''}`}
              </Button>
              {skippedPaths?.size > 0 && (
                <span className="text-xs text-muted-foreground">{skippedPaths?.size} file{skippedPaths?.size !== 1 ? 's' : ''} will be skipped</span>
              )}
              <Button variant="outline" onClick={() => { setZipFile(null); setPreviews([]); setOverrides({}); setSkippedPaths(new Set()); }}>
                Clear
              </Button>
            </div>
          )}

          {/* Progress */}
          {ingesting && ingestProgress && (
            <div className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-foreground truncate max-w-xs">Processing: {ingestProgress?.filename}</span>
                <span className="text-sm text-muted-foreground flex-shrink-0">{ingestProgress?.current}/{ingestProgress?.total}</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: ingestProgress?.total > 0 ? `${(ingestProgress?.current / ingestProgress?.total) * 100}%` : '0%' }}></div>
              </div>
            </div>
          )}

          {/* Post-Ingest Summary */}
          {ingestResult && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-6">
              <h4 className="font-bold text-green-400 text-lg mb-4 flex items-center gap-2">
                <Icon name="CheckCircle" size={20} />
                Ingest Complete
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  { label: 'Parsed Successfully', value: ingestResult?.succeeded, color: 'text-green-400', icon: 'CheckCircle' },
                  { label: 'Uploaded', value: ingestResult?.succeeded, color: 'text-blue-400', icon: 'Upload' },
                  { label: 'Needs Review', value: ingestResult?.needsReview, color: 'text-orange-400', icon: 'AlertTriangle' },
                  { label: 'Failed', value: ingestResult?.failed, color: 'text-red-400', icon: 'XCircle' },
                ]?.map(({ label, value, color, icon }) => (
                  <div key={label} className="bg-card/60 rounded-xl p-4 text-center border border-border/50">
                    <Icon name={icon} size={20} className={`mx-auto mb-1 ${color}`} />
                    <p className={`text-2xl font-bold ${color}`}>{value ?? 0}</p>
                    <p className="text-xs text-muted-foreground mt-1">{label}</p>
                  </div>
                ))}
              </div>
              {(ingestResult?.skipped > 0) && (
                <p className="text-xs text-muted-foreground mt-3">{ingestResult?.skipped} file{ingestResult?.skipped !== 1 ? 's' : ''} skipped (invalid extension or manually excluded).</p>
              )}
              <Button variant="outline" size="sm" className="mt-4" onClick={() => { setZipFile(null); setPreviews([]); setIngestResult(null); setOverrides({}); setSkippedPaths(new Set()); }}>
                Upload Another ZIP
              </Button>
            </div>
          )}
        </div>
      )}
      {/* Metadata Editor Modal */}
      {editingPreview && (
        <MetadataEditor
          preview={editingPreview}
          onSave={(form) => handleSaveOverride(editingPreview?.relativePath, form)}
          onClose={() => setEditingPreview(null)}
        />
      )}
    </div>
  );
};

// ─── INGEST JOBS TAB ──────────────────────────────────────────────────────────

const IngestJobsTab = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobItems, setJobItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);

  useEffect(() => {
    const fetchJobs = async () => {
      setLoading(true);
      try { setJobs(await getIngestJobs()); } catch (err) { console.error(err); } finally { setLoading(false); }
    };
    fetchJobs();
  }, []);

  const handleViewJob = async (job) => {
    setSelectedJob(job);
    setLoadingItems(true);
    try { setJobItems(await getIngestJobItems(job?.id)); } catch (err) { console.error(err); } finally { setLoadingItems(false); }
  };

  const handleExportCSV = (items) => {
    const csv = exportFailedItemsCSV(items);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ingest-failures-${selectedJob?.id?.slice(0, 8)}.csv`;
    a?.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : jobs?.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <Icon name="ClipboardList" size={48} className="mx-auto mb-3 opacity-30" />
          <p>No ingest jobs yet. Upload a ZIP to get started.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {jobs?.map(job => (
            <div key={job?.id} className="bg-card border border-border rounded-xl p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="Package" size={20} className="text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">{job?.filename}</p>
                    <p className="text-xs text-muted-foreground">{new Date(job.created_at)?.toLocaleString()}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-2 py-1 text-xs rounded-full border ${STATUS_COLORS?.[job?.status] || STATUS_COLORS?.pending}`}>
                    {job?.status}
                  </span>
                  <div className="flex gap-2 text-xs text-muted-foreground">
                    <span className="text-green-400">✓ {job?.succeeded}</span>
                    <span className="text-red-400">✗ {job?.failed}</span>
                    <span className="text-yellow-400">⊘ {job?.skipped}</span>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => handleViewJob(job)}>Details</Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Job Detail Modal */}
      {selectedJob && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setSelectedJob(null)}>
          <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-2xl mx-4 shadow-2xl" onClick={e => e?.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-foreground">{selectedJob?.filename}</h3>
              <div className="flex gap-2">
                {jobItems?.some(i => i?.status === 'failed' || i?.status === 'ambiguous') && (
                  <Button variant="outline" size="sm" iconName="Download" onClick={() => handleExportCSV(jobItems)}>Export CSV</Button>
                )}
                <button onClick={() => setSelectedJob(null)} className="text-muted-foreground hover:text-foreground">
                  <Icon name="X" size={20} />
                </button>
              </div>
            </div>
            <div className="overflow-y-auto flex-1">
              {loadingItems ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                </div>
              ) : (
                <div className="space-y-2">
                  {jobItems?.map(item => (
                    <div key={item?.id} className="flex items-start gap-3 p-3 bg-muted/20 rounded-lg">
                      <span className={`px-2 py-1 text-xs rounded-full border flex-shrink-0 ${STATUS_COLORS?.[item?.status] || STATUS_COLORS?.pending}`}>
                        {item?.status}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-mono text-foreground truncate">{item?.original_filename}</p>
                        {item?.parsed_category && <p className="text-xs text-muted-foreground">Category: {item?.parsed_category}</p>}
                        {item?.error_message && <p className="text-xs text-red-400 mt-1">{item?.error_message}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── RULES MANAGER TAB ────────────────────────────────────────────────────────

const RulesManagerTab = ({ categories }) => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [form, setForm] = useState({ rule_type: 'disables', trigger_slug: '', trigger_category: '', affected_category: '', affected_slug: '', description: '', is_active: true });

  const fetchRules = async () => {
    setLoading(true);
    try { setRules(await getRules()); } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  useEffect(() => { fetchRules(); }, []);

  const handleSave = async () => {
    try {
      await upsertRule(editingRule ? { ...form, id: editingRule?.id } : form);
      setShowForm(false);
      setEditingRule(null);
      setForm({ rule_type: 'disables', trigger_slug: '', trigger_category: '', affected_category: '', affected_slug: '', description: '', is_active: true });
      fetchRules();
    } catch (err) { console.error(err); }
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setForm({ rule_type: rule?.rule_type, trigger_slug: rule?.trigger_slug || '', trigger_category: rule?.trigger_category || '', affected_category: rule?.affected_category || '', affected_slug: rule?.affected_slug || '', description: rule?.description || '', is_active: rule?.is_active });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this rule?')) return;
    try { await deleteRule(id); fetchRules(); } catch (err) { console.error(err); }
  };

  const RULE_TYPE_COLORS = { disables: 'text-red-400', conflicts_with: 'text-orange-400', requires: 'text-blue-400' };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-muted-foreground">Define which assets conflict, disable, or require other assets.</p>
        <Button variant="default" iconName="Plus" onClick={() => { setEditingRule(null); setShowForm(true); }}>Add Rule</Button>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : rules?.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <Icon name="GitBranch" size={48} className="mx-auto mb-3 opacity-30" />
          <p>No compatibility rules yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rules?.map(rule => (
            <div key={rule?.id} className="bg-card border border-border rounded-xl p-4 flex items-center gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-mono text-foreground">{rule?.trigger_slug || rule?.trigger_category || 'any'}</span>
                  <span className={`text-sm font-bold ${RULE_TYPE_COLORS?.[rule?.rule_type]}`}>{rule?.rule_type}</span>
                  <span className="text-sm font-mono text-foreground">{rule?.affected_slug || rule?.affected_category || 'unknown'}</span>
                </div>
                {rule?.description && <p className="text-xs text-muted-foreground mt-1">{rule?.description}</p>}
              </div>
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${rule?.is_active ? 'bg-green-400' : 'bg-muted-foreground'}`}></span>
                <button onClick={() => handleEdit(rule)} className="p-1.5 hover:bg-muted rounded-lg transition-colors">
                  <Icon name="Pencil" size={14} />
                </button>
                <button onClick={() => handleDelete(rule?.id)} className="p-1.5 hover:bg-red-500/10 text-red-400 rounded-lg transition-colors">
                  <Icon name="Trash2" size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Rule Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setShowForm(false)}>
          <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-lg mx-4 shadow-2xl" onClick={e => e?.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-foreground">{editingRule ? 'Edit Rule' : 'New Rule'}</h3>
              <button onClick={() => setShowForm(false)} className="text-muted-foreground hover:text-foreground"><Icon name="X" size={20} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Rule Type</label>
                <select value={form?.rule_type} onChange={e => setForm(f => ({ ...f, rule_type: e?.target?.value }))}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm">
                  <option value="disables">disables</option>
                  <option value="conflicts_with">conflicts_with</option>
                  <option value="requires">requires</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Trigger Slug (optional)</label>
                  <input type="text" value={form?.trigger_slug} onChange={e => setForm(f => ({ ...f, trigger_slug: e?.target?.value }))}
                    placeholder="e.g. donkey_head_jester"
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Trigger Category (optional)</label>
                  <select value={form?.trigger_category} onChange={e => setForm(f => ({ ...f, trigger_category: e?.target?.value }))}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm">
                    <option value="">None</option>
                    {categories?.map(c => <option key={c?.id} value={c?.slug}>{c?.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Affected Category</label>
                  <select value={form?.affected_category} onChange={e => setForm(f => ({ ...f, affected_category: e?.target?.value }))}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm">
                    <option value="">None</option>
                    {categories?.map(c => <option key={c?.id} value={c?.slug}>{c?.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Affected Slug (optional)</label>
                  <input type="text" value={form?.affected_slug} onChange={e => setForm(f => ({ ...f, affected_slug: e?.target?.value }))}
                    placeholder="e.g. hair_long_brown"
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Description</label>
                <input type="text" value={form?.description} onChange={e => setForm(f => ({ ...f, description: e?.target?.value }))}
                  placeholder="Human-readable explanation"
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm" />
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={form?.is_active} onChange={e => setForm(f => ({ ...f, is_active: e?.target?.checked }))} className="w-4 h-4 rounded" />
                <span className="text-sm text-foreground">Active</span>
              </label>
            </div>
            <div className="flex gap-3 mt-6">
              <Button variant="default" fullWidth onClick={handleSave}>Save Rule</Button>
              <Button variant="outline" fullWidth onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── PRESETS & COLLECTIONS TAB ────────────────────────────────────────────────

const PresetsCollectionsTab = () => {
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingPreset, setEditingPreset] = useState(null);
  const [form, setForm] = useState({ name: '', slug: '', description: '', is_student_visible: true, avatar_config: '{}' });

  const fetchPresets = async () => {
    setLoading(true);
    try { setPresets(await getPresets()); } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  useEffect(() => { fetchPresets(); }, []);

  const handleSave = async () => {
    try {
      let config = {};
      try { config = JSON.parse(form?.avatar_config); } catch {}
      await upsertPreset({ ...form, avatar_config: config, ...(editingPreset ? { id: editingPreset?.id } : {}) });
      setShowForm(false);
      setEditingPreset(null);
      fetchPresets();
    } catch (err) { console.error(err); }
  };

  const handleEdit = (preset) => {
    setEditingPreset(preset);
    setForm({ name: preset?.name, slug: preset?.slug, description: preset?.description || '', is_student_visible: preset?.is_student_visible, avatar_config: JSON.stringify(preset?.avatar_config || {}, null, 2) });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this preset?')) return;
    try { await deletePreset(id); fetchPresets(); } catch (err) { console.error(err); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-muted-foreground">Create themed avatar presets for students to choose from.</p>
        <Button variant="default" iconName="Plus" onClick={() => { setEditingPreset(null); setForm({ name: '', slug: '', description: '', is_student_visible: true, avatar_config: '{}' }); setShowForm(true); }}>New Preset</Button>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : presets?.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <Icon name="Sparkles" size={48} className="mx-auto mb-3 opacity-30" />
          <p>No presets yet. Create themed avatar combinations for students.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {presets?.map(preset => (
            <div key={preset?.id} className="bg-card border border-border rounded-xl p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="font-semibold text-foreground">{preset?.name}</h4>
                  <p className="text-xs text-muted-foreground font-mono">{preset?.slug}</p>
                </div>
                <div className="flex items-center gap-1">
                  {preset?.is_student_visible && (
                    <span className="px-2 py-0.5 text-xs bg-green-500/20 text-green-400 border border-green-500/30 rounded-full">Visible</span>
                  )}
                </div>
              </div>
              {preset?.description && <p className="text-sm text-muted-foreground mb-3">{preset?.description}</p>}
              <div className="bg-muted/30 rounded-lg p-2 mb-3">
                <pre className="text-xs text-muted-foreground overflow-hidden max-h-20">{JSON.stringify(preset?.avatar_config, null, 2)}</pre>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" fullWidth onClick={() => handleEdit(preset)}>Edit</Button>
                <button onClick={() => handleDelete(preset?.id)} className="p-2 hover:bg-red-500/10 text-red-400 rounded-lg transition-colors">
                  <Icon name="Trash2" size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Preset Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setShowForm(false)}>
          <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-lg mx-4 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e?.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-foreground">{editingPreset ? 'Edit Preset' : 'New Preset'}</h3>
              <button onClick={() => setShowForm(false)} className="text-muted-foreground hover:text-foreground"><Icon name="X" size={20} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Name</label>
                <input type="text" value={form?.name} onChange={e => setForm(f => ({ ...f, name: e?.target?.value, slug: e?.target?.value?.toLowerCase()?.replace(/\s+/g, '-')?.replace(/[^a-z0-9-]/g, '') }))}
                  placeholder="e.g. Hamlet Scholar"
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm" />
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Slug</label>
                <input type="text" value={form?.slug} onChange={e => setForm(f => ({ ...f, slug: e?.target?.value }))}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm font-mono" />
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Description</label>
                <input type="text" value={form?.description} onChange={e => setForm(f => ({ ...f, description: e?.target?.value }))}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm" />
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Avatar Config (JSON)</label>
                <textarea value={form?.avatar_config} onChange={e => setForm(f => ({ ...f, avatar_config: e?.target?.value }))} rows={6}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm font-mono resize-none" />
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={form?.is_student_visible} onChange={e => setForm(f => ({ ...f, is_student_visible: e?.target?.checked }))} className="w-4 h-4 rounded" />
                <span className="text-sm text-foreground">Visible to Students</span>
              </label>
            </div>
            <div className="flex gap-3 mt-6">
              <Button variant="default" fullWidth onClick={handleSave}>Save Preset</Button>
              <Button variant="outline" fullWidth onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── SETTINGS TAB ─────────────────────────────────────────────────────────────

const SettingsTab = ({ categories, onRefresh }) => {
  const [bulkAction, setBulkAction] = useState('');
  const [selectedCat, setSelectedCat] = useState('');
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState('');

  // Storage Health state
  const [bucketHealth, setBucketHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [lastIngestJob, setLastIngestJob] = useState(null);

  useEffect(() => {
    checkBucketHealth();
    fetchLastIngestJob();
  }, []);

  const checkBucketHealth = async () => {
    setHealthLoading(true);
    try {
      const health = await validateAvatarBucket();
      setBucketHealth(health);
    } catch (err) {
      setBucketHealth({ exists: false, isPublic: false, error: err?.message });
    } finally {
      setHealthLoading(false);
    }
  };

  const fetchLastIngestJob = async () => {
    try {
      const jobs = await getIngestJobs();
      setLastIngestJob(jobs?.[0] || null);
    } catch (err) {
      // non-critical
    }
  };

  const handleBulkApply = async () => {
    if (!bulkAction) return;
    setApplying(true);
    setResult('');
    try {
      const { assets } = await getAssets({ categorySlug: selectedCat || undefined, isArchived: false });
      const ids = assets?.map(a => a?.id);
      const updates = {
        activate_all: { is_active: true },
        deactivate_all: { is_active: false },
        show_all: { is_student_visible: true },
        hide_all: { is_student_visible: false },
      }?.[bulkAction];
      if (updates && ids?.length > 0) {
        await bulkUpdateAssets(ids, updates);
        setResult(`Applied "${bulkAction}" to ${ids?.length} assets.`);
        onRefresh?.();
      }
    } catch (err) {
      setResult('Error: ' + err?.message);
    } finally {
      setApplying(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return 'Never';
    return new Date(iso)?.toLocaleString();
  };

  return (
    <div className="max-w-2xl space-y-6">

      {/* ── STORAGE HEALTH ─────────────────────────────────────────────────── */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${bucketHealth?.exists ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
              <Icon name={bucketHealth?.exists ? 'HardDrive' : 'AlertTriangle'} size={18} className={bucketHealth?.exists ? 'text-green-400' : 'text-red-400'} />
            </div>
            <h3 className="font-semibold text-foreground">Storage Health</h3>
          </div>
          <button
            onClick={checkBucketHealth}
            disabled={healthLoading}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded-md hover:bg-muted/50">
            <Icon name={healthLoading ? 'Loader2' : 'RefreshCw'} size={13} className={healthLoading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>

        {healthLoading && !bucketHealth ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
            <Icon name="Loader2" size={14} className="animate-spin" />
            Checking bucket status…
          </div>
        ) : (
          <>
            {/* Missing bucket warning banner */}
            {bucketHealth && !bucketHealth?.exists && (
              <div className="flex items-start gap-3 bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
                <Icon name="AlertTriangle" size={18} className="text-red-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-red-400 mb-1">Bucket Not Found</p>
                  <p className="text-xs text-red-300/80">
                    {bucketHealth?.error || `The storage bucket '${AVATAR_ASSET_BUCKET}' does not exist. ZIP ingestion is disabled until the bucket is created.`}
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Run the migration <code className="bg-muted px-1 rounded">20260315002654_avatar_assets_bucket_policies.sql</code> in your Supabase project to create the bucket.
                  </p>
                </div>
              </div>
            )}

            {/* Health metrics grid */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-muted/30 rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-1">Bucket Name</p>
                <p className="text-sm font-mono text-foreground">{AVATAR_ASSET_BUCKET}</p>
              </div>
              <div className="bg-muted/30 rounded-lg p-3">
                <p className="text-xs text-muted-foreground">
                  Upload path: <code className="bg-muted px-1 rounded">{AVATAR_ASSET_VERSION}/{'{category}'}/{'{filename}'}</code>
                </p>
              </div>
            </div>

            {/* Path structure info */}
            {bucketHealth?.exists && (
              <div className="mt-3 pt-3 border-t border-border/50">
                <p className="text-xs text-muted-foreground">
                  Upload path: <code className="bg-muted px-1 rounded">{AVATAR_ASSET_VERSION}/{'{category}'}/{'{filename}'}</code>
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── BULK PUBLISHING ────────────────────────────────────────────────── */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="font-semibold text-foreground mb-1">Bulk Publishing Controls</h3>
        <p className="text-sm text-muted-foreground mb-2">Apply visibility and activation changes across asset groups.</p>
        <div className="flex flex-wrap gap-3 mb-4">
          <select value={selectedCat} onChange={e => setSelectedCat(e?.target?.value)}
            className="px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm">
            <option value="">All Categories</option>
            {categories?.map(c => <option key={c?.id} value={c?.slug}>{c?.label}</option>)}
          </select>
          <select value={bulkAction} onChange={e => setBulkAction(e?.target?.value)}
            className="px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm">
            <option value="">Select Action</option>
            <option value="activate_all">Activate All</option>
            <option value="deactivate_all">Deactivate All</option>
            <option value="show_all">Show to Students</option>
            <option value="hide_all">Hide from Students</option>
          </select>
          <Button variant="default" onClick={handleBulkApply} disabled={applying || !bulkAction}>
            {applying ? 'Applying...' : 'Apply'}
          </Button>
        </div>
        {result && <p className="text-sm text-green-400">{result}</p>}
      </div>

      {/* ── STORAGE BUCKET INFO ────────────────────────────────────────────── */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="font-semibold text-foreground mb-1">Storage Bucket</h3>
        <p className="text-sm text-muted-foreground mb-2">Avatar assets are stored in the <code className="bg-muted px-1 rounded">{AVATAR_ASSET_BUCKET}</code> bucket.</p>
        <p className="text-sm text-muted-foreground">Path structure: <code className="bg-muted px-1 rounded">{AVATAR_ASSET_VERSION}/{'{category}'}/{'{filename}'}</code></p>
      </div>

      {/* ── NAMING CONVENTIONS ─────────────────────────────────────────────── */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="font-semibold text-foreground mb-1">Naming Conventions</h3>
        <div className="space-y-1 text-sm text-muted-foreground font-mono">
          {[
            'body_male.jpg → bodies, body_type=male',
            'eyes_happy_blue.jpg → eyes, style=happy, eye_color=blue',
            'hair_female_long_brown.jpg → hair_styles, gender=female, style=long, color=brown',
            'clothing_doublet_burgundy.jpg → clothing, style=doublet, color=burgundy',
            'wings_fairy_large_purple.png → fantasy, special_type=wings_fairy, size=large, color=purple',
            'donkey_head_jester.png → fantasy, special_type=donkey_head, variant=jester',
          ]?.map((ex, i) => <p key={i} className="py-1 border-b border-border/50 last:border-0">{ex}</p>)}
        </div>
      </div>
    </div>
  );
}
// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

export default function AdminAvatarAssetStudio() {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('library');
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    document.title = 'Avatar Asset Studio | BardPlay Admin';
    getCategories()?.then(setCategories)?.catch(console.error);
  }, []);

  const isAuthorized = ['admin', 'superadmin', 'editor']?.includes(userProfile?.role);

  if (!isAuthorized) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Lock" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-xl font-bold text-foreground mb-2">Access Restricted</h2>
          <p className="text-muted-foreground mb-4">Avatar Asset Studio requires editor or admin access.</p>
          <Button variant="outline" onClick={() => navigate('/admin-dashboard')}>Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedNavigation user={user} isAuthenticated={true} notifications={[]} />
      <main className="main-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <BreadcrumbTrail />

          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg">
                <Icon name="Wand2" size={28} color="white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground">Avatar Asset Studio</h1>
                <p className="text-sm text-muted-foreground mb-2">Manage Shakespearean avatar assets, rules, and presets</p>
              </div>
            </div>
            <Button variant="outline" iconName="ArrowLeft" onClick={() => navigate('/admin-dashboard')}>Dashboard</Button>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-8 bg-muted/30 p-1 rounded-xl overflow-x-auto">
            {TABS?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 ${activeTab === tab?.id ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
              >
                <Icon name={tab?.icon} size={16} />
                {tab?.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div>
            {activeTab === 'library' && <AssetLibraryTab categories={categories} />}
            {activeTab === 'ingest' && <ZipIngestTab userId={user?.id} />}
            {activeTab === 'jobs' && <IngestJobsTab />}
            {activeTab === 'rules' && <RulesManagerTab categories={categories} />}
            {activeTab === 'presets' && <PresetsCollectionsTab />}
            {activeTab === 'settings' && <SettingsTab categories={categories} onRefresh={() => getCategories()?.then(setCategories)?.catch(console.error)} />}
          </div>
        </div>
      </main>
    </div>
  );
}
